// Pair-independent lifecycle recovery. No pricing or settlement decisions belong here.
export class CandleRecovery {
  private running: Promise<void> | null = null;
  private pending = false;
  private stopped = false;
  private suspension = 0;
  private lastCheck = Date.now();
  private lastAttempt = -Infinity;
  private timer: ReturnType<typeof setInterval> | null = null;
  private removers: Array<() => void> = [];

  constructor(private hooks: { pause: () => void; recover: () => Promise<void>; ready: () => void }) {}

  public suspend = () => { ++this.suspension; this.pending = true; };

  public request = (): Promise<void> => {
    if (this.stopped || document.hidden) return Promise.resolve();
    if (this.running) return this.running;
    if (!this.pending && Date.now() - this.lastAttempt < 5000) return Promise.resolve();
    this.pending = true;
    this.lastAttempt = Date.now();
    const suspension = this.suspension;
    let deadline: ReturnType<typeof setTimeout>;
    const timeout = new Promise<void>((_, reject) => { deadline = setTimeout(() => reject(new Error('Recovery timed out')), 15000); });
    this.running = Promise.race([Promise.resolve().then(() => this.hooks.recover()), timeout]).then(() => {
      if (this.stopped || document.hidden || suspension !== this.suspension) return;
      this.pending = false;
      this.hooks.ready();
    }).catch(() => {
      // Keep watchdog retrying gracefully in the background
      this.pending = true;
    }).finally(() => { clearTimeout(deadline); this.running = null; });
    return this.running;
  };

  public check = () => {
    const now = Date.now();
    const elapsed = now - this.lastCheck;
    this.lastCheck = now;
    if (elapsed < 0) this.lastAttempt = -Infinity;
    if (elapsed > 60000 || elapsed < 0) this.pending = true;
    if (this.pending && now - this.lastAttempt >= 10000) void this.request();
  };

  public start() {
    if (this.timer || this.stopped) return;
    const listen = (target: EventTarget, event: string, handler: EventListener) => {
      target.addEventListener(event, handler);
      this.removers.push(() => target.removeEventListener(event, handler));
    };
    let hiddenAt = 0;
    listen(document, 'visibilitychange', () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        this.suspend();
      } else {
        const awayMs = hiddenAt > 0 ? Date.now() - hiddenAt : 0;
        if (awayMs > 60000) {
          this.pending = true;
          void this.request();
        }
      }
    });
    listen(document, 'freeze', () => { hiddenAt = Date.now(); this.suspend(); });
    listen(document, 'resume', () => {
      this.pending = true;
      void this.request();
    });
    listen(window, 'pagehide', () => { hiddenAt = Date.now(); this.suspend(); });
    listen(window, 'pageshow', () => {
      const awayMs = hiddenAt > 0 ? Date.now() - hiddenAt : 0;
      if (awayMs > 60000) {
        this.pending = true;
        void this.request();
      }
    });
    listen(window, 'online', () => {
      this.pending = true;
      void this.request();
    });
    this.timer = setInterval(this.check, 1000);
  }

  public destroy() {
    this.stopped = true;
    if (this.timer) clearInterval(this.timer);
    this.removers.splice(0).forEach(remove => remove());
  }
}

// Deduplicate overlapping history requests for the current selection only.
// A -> B -> A must create a new A request, not revive its obsolete response.
export class CandleRequests {
  private slot?: { key: string; promise: Promise<boolean> };
  run(key: string, fetch: () => Promise<boolean>): Promise<boolean> {
    if (this.slot?.key === key) return this.slot.promise;
    const slot = { key, promise: fetch() };
    this.slot = slot;
    void slot.promise.finally(() => { if (this.slot === slot) this.slot = undefined; }).catch(() => {});
    return slot.promise;
  }
}
